// Dialog: the kit's modal. The scrim blurs the page, the panel grows out of the control that
// opened it, holds focus while it is up and hands it back when it goes. On a phone it rises from
// the bottom edge as a sheet. docs/operations/design-standard.md, "Controls".

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, IconButton } from './Button';
import { IconClose } from './icons';
import { useScrollLock } from './DialogScrollLock';
import { MS, usePresence } from './motion';
import Tooltip from './Tooltip';

let seq = 0;
/** Open dialogs, newest last. An Escape that lands outside every panel belongs to the top one. */
const stack: number[] = [];

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export interface DialogProps {
  open: boolean;
  /** Escape, the scrim and the close key ask for this. Never called while `busy`. */
  onClose: () => void;
  title?: React.ReactNode;
  /** The accessible name, when the title is not plain words or there is none. */
  label?: string;
  /** A line under the title: what it is about, the step you are on. */
  meta?: React.ReactNode;
  children?: React.ReactNode;
  /** The actions, pinned to the bottom edge one tone step up. The main action goes last. */
  footer?: React.ReactNode;
  /** What stopped the action, said beside the actions and read out when it changes. */
  problem?: React.ReactNode;
  /** Makes the panel a form: Enter in a field submits, and a submit key in the footer works. The browser's own field checks do not run. */
  onSubmit?: () => void;
  /** sm 24rem (a PIN), md 28rem (a question), lg 40rem (a form), xl 48rem (a wide form), 2xl 64rem (a viewer: a log, a map). */
  size?: DialogSize;
  /** `danger` sets the title in the error tone, for a question about something destructive. */
  tone?: 'default' | 'danger';
  /** A question that must be answered first (a confirm): role alertdialog, above every other overlay. */
  alert?: boolean;
  /** Where focus lands. Without it: a field that asked with autoFocus, else the first field, else the first key. */
  initialFocus?: React.RefObject<HTMLElement | null>;
  /** Where focus goes on close. Without it, whatever had focus when the dialog opened. */
  returnTo?: React.RefObject<HTMLElement | null>;
  /** False: a click on the scrim does nothing. For forms holding typed work. */
  dismissible?: boolean;
  /** False: Escape does nothing either, for a form whose close throws away what was typed. */
  closeOnEscape?: boolean;
  /**
   * Typed work would be lost. Escape, the scrim and the close key ask "Discard your changes?"
   * first; the footer's own Cancel still closes at once, since pressing it is the answer.
   */
  dirty?: boolean;
  /** Work in flight: Escape, the scrim and the close key wait for it. */
  busy?: boolean;
  /** What the close key does, for its tooltip ("Close without saving"). */
  closeHint?: string;
  /** No close key in the header; Cancel in the footer is the way out. */
  hideClose?: boolean;
  /** Drawn on white, for a dialog that belongs to a day-mode page (the schedules pass their flag). */
  light?: boolean;
  /** Runs once the exit has finished, for a parent that unmounts the dialog afterwards. */
  onExited?: () => void;
}

const WIDTH: Record<DialogSize, string> = {
  sm: 'sm:max-w-[24rem]',
  md: 'sm:max-w-[28rem]',
  lg: 'sm:max-w-[40rem]',
  xl: 'sm:max-w-[48rem]',
  '2xl': 'sm:max-w-[64rem]',
};

const DARK = {
  panel: 'bg-surface-container-low text-on-surface',
  footer: 'bg-surface-container',
  title: 'text-on-surface',
  danger: 'text-red-300',
  meta: 'text-on-surface-variant',
  problem: 'text-red-300',
  close: '',
};
const LIGHT = {
  panel: 'bg-white text-gray-900',
  footer: 'bg-[#F4F4F4]',
  title: 'text-gray-900',
  danger: 'text-red-700',
  meta: 'text-gray-700',
  problem: 'text-red-700',
  close: '!text-gray-700 hover:!text-gray-900',
};

const FIELD = 'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled])';
const TABBABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

const visible = (el: HTMLElement) => !el.closest('[aria-hidden="true"]');
export const tabbablesIn = (root: HTMLElement): HTMLElement[] =>
  Array.from(root.querySelectorAll<HTMLElement>(TABBABLE)).filter((el) => el.tabIndex >= 0 && visible(el));

export const Dialog: React.FC<DialogProps> = (props) => {
  const { open, dismissible = true, initialFocus, returnTo } = props;
  const [id] = useState(() => ++seq);
  // Presence starts closed for one render, so a dialog mounted already open still arrives.
  const [armed, setArmed] = useState(false);
  useEffect(() => { setArmed(true); }, []);
  const { mounted, open: shown } = usePresence(open && armed, MS.exit);

  const panelRef = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  const [origin, setOrigin] = useState<string | undefined>(undefined);

  const latest = useRef(props);
  latest.current = props;
  // What leaves is what was last shown: the panel keeps its content while it fades.
  const last = useRef(props);
  if (open) last.current = props;
  const v = last.current;
  const lastProblem = useRef<React.ReactNode>(null);
  if (v.problem) lastProblem.current = v.problem;

  const [asking, setAsking] = useState(false);
  const keepRef = useRef<HTMLButtonElement>(null);
  // Where Keep editing hands focus back: the field the person was in. Never the header's close
  // key (its tooltip would take the next Escape) and never the page behind.
  const resumeAt = useRef<HTMLElement | null>(null);
  useEffect(() => { if (!open) setAsking(false); }, [open]);

  const requestClose = () => {
    if (latest.current.busy) return;
    if (!latest.current.dirty) { latest.current.onClose(); return; }
    const panel = panelRef.current;
    const at = document.activeElement as HTMLElement | null;
    const inBody = !!(panel && at && panel.contains(at) && !at.closest('[data-dialog-header]'));
    resumeAt.current = inBody ? at : (panel && (Array.from(panel.querySelectorAll<HTMLElement>(FIELD)).find(visible) || panel)) || null;
    setAsking(true);
  };

  const giveBack = () => {
    const back = latest.current.returnTo?.current || opener.current;
    if (back && back.isConnected && typeof back.focus === 'function') back.focus();
  };

  useScrollLock(open);

  useEffect(() => {
    if (!open) return undefined;
    stack.push(id);
    return () => {
      const at = stack.indexOf(id);
      if (at >= 0) stack.splice(at, 1);
    };
  }, [open, id]);

  // Runs before the panel mounts (presence mounts it a render later), so it reads the opener.
  useEffect(() => {
    if (open) {
      if (!wasOpen.current) opener.current = document.activeElement as HTMLElement | null;
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    giveBack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A parent that drops the dialog while it is open still gets focus back.
  useEffect(() => () => { if (wasOpen.current) giveBack(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const wasMounted = useRef(false);
  useEffect(() => {
    if (mounted) { wasMounted.current = true; return; }
    if (!wasMounted.current) return;
    wasMounted.current = false;
    latest.current.onExited?.();
  }, [mounted]);

  // The panel grows out of its opener: measured once, before the first paint.
  useLayoutEffect(() => {
    if (!mounted) return;
    const panel = panelRef.current;
    const from = returnTo?.current || opener.current;
    if (!panel || !from || from === document.body || !from.isConnected) { setOrigin(undefined); return; }
    const a = from.getBoundingClientRect();
    const p = panel.getBoundingClientRect();
    if (!a.width && !a.height) { setOrigin(undefined); return; }
    setOrigin(`${Math.round(a.left + a.width / 2 - p.left)}px ${Math.round(a.top + a.height / 2 - p.top)}px`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted]);

  useEffect(() => {
    if (!open || !mounted) return;
    const panel = panelRef.current;
    if (!panel) return;
    const asked = initialFocus?.current;
    // A call site names its field with data-drawer-autofocus, on the field or on a wrapper around it.
    const mark = Array.from(panel.querySelectorAll<HTMLElement>('[data-drawer-autofocus]')).find(visible);
    const marked = mark && (mark.matches(FIELD) || mark.matches(TABBABLE) ? mark : mark.querySelector<HTMLElement>(FIELD));
    // A field that asked with autoFocus already has it.
    if (!asked && !marked && panel.contains(document.activeElement)) return;
    const field = Array.from(panel.querySelectorAll<HTMLElement>(FIELD)).find(visible);
    // Never the header's close key: its tooltip would take the first Escape.
    const key = tabbablesIn(panel).find((el) => !el.closest('[data-dialog-header]'));
    const target = asked || marked || field || key || panel;
    target.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mounted]);

  // Escape that lands outside every panel (focus on the page after a click on plain text).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || stack[stack.length - 1] !== id) return;
      if (panelRef.current?.contains(e.target as Node)) return;
      e.preventDefault();
      if (latest.current.closeOnEscape !== false) requestClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, id]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const panel = panelRef.current;
    if (!panel) return;
    if (e.key === 'Escape') {
      // A widget inside that used the Escape (a menu closing) marks it handled.
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      // Firefox passes on the Escape that closed a native select's list.
      if ((e.target as HTMLElement).tagName === 'SELECT') return;
      e.preventDefault();
      // Stopped, so a dialog that holds this one in its React tree does not close too.
      e.stopPropagation();
      if (latest.current.closeOnEscape !== false) requestClose();
      return;
    }
    if (e.key !== 'Tab' || !panel.contains(e.target as Node)) return;
    const items = tabbablesIn(panel);
    if (items.length === 0) { e.preventDefault(); return; }
    const first = items[0];
    const end = items[items.length - 1];
    const at = document.activeElement;
    if (e.shiftKey && (at === first || at === panel)) { e.preventDefault(); end.focus(); }
    else if (!e.shiftKey && at === end) { e.preventDefault(); first.focus(); }
  };

  if (!mounted || typeof document === 'undefined') return null;

  const s = v.light ? LIGHT : DARK;
  const titleId = `ui-dialog-${id}-title`;
  const bodyId = `ui-dialog-${id}-body`;
  const size = v.size || 'md';

  const header = v.title ? (
    <div data-dialog-header className="flex flex-none items-start justify-between gap-3 px-5 pb-2 pt-4">
      <div className="min-w-0 flex-1">
        <h2 id={titleId} className={`m-0 text-balance text-base font-semibold leading-6 [overflow-wrap:anywhere] ${v.tone === 'danger' ? s.danger : s.title}`}>
          {v.title}
        </h2>
        {v.meta ? <div className={`mt-1 text-[13px] leading-[18px] ${s.meta}`}>{v.meta}</div> : null}
      </div>
      {v.hideClose ? null : (
        <Tooltip label={v.closeHint || 'Close'}>
          <IconButton label="Close" size="sm" disabled={v.busy} onClick={requestClose} className={`-mr-1.5 ${s.close}`}>
            <IconClose />
          </IconButton>
        </Tooltip>
      )}
    </div>
  ) : null;

  const body = (
    <div id={bodyId} className={`min-h-0 flex-1 overflow-y-auto px-5 pb-5 ${v.title ? 'pt-1' : 'pt-5'}`}>
      {v.children}
    </div>
  );

  const footer = v.footer || v.problem ? (
    <div className={`flex flex-none flex-wrap items-center justify-end gap-2 px-5 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:rounded-b-overlay sm:pb-3 ${s.footer}`}>
      <span
        role="alert"
        aria-hidden={v.problem ? undefined : true}
        className={`mr-auto min-w-0 text-sm leading-5 ${s.problem} transition-opacity ${v.problem ? 'opacity-100 duration-standard ease-emphasized' : 'opacity-0 duration-exit ease-exit'}`}
      >
        {v.problem ? v.problem : lastProblem.current}
      </span>
      {v.footer}
    </div>
  ) : null;

  const content = (
    <>
      {header}
      {body}
      {footer}
    </>
  );

  return createPortal(
    <div className={`fixed inset-0 ${v.alert ? 'z-[100]' : 'z-50'} flex items-end justify-center pt-12 sm:items-center sm:p-4 ${open ? '' : 'pointer-events-none'}`}>
      <div
        aria-hidden="true"
        // Kept from taking focus when it will ask instead of closing, so the field keeps it.
        onMouseDown={(e) => { if (!dismissible || latest.current.dirty) e.preventDefault(); }}
        onClick={() => { if (dismissible) requestClose(); }}
        className={`scrim transition-opacity ${shown ? 'opacity-100 duration-standard ease-emphasized' : 'opacity-0 duration-exit ease-exit'}`}
      />
      <div
        ref={panelRef}
        role={v.alert ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={v.label || !v.title ? undefined : titleId}
        aria-label={v.label || (v.title ? undefined : 'Dialog')}
        aria-describedby={v.alert ? bodyId : undefined}
        aria-busy={v.busy || undefined}
        aria-hidden={open ? undefined : true}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        style={origin ? { transformOrigin: origin } : undefined}
        className={`relative flex max-h-full w-full min-w-0 flex-col rounded-t-overlay shadow-overlay outline-none sm:rounded-overlay ${WIDTH[size]} ${s.panel} transition-[transform,opacity] motion-reduce:translate-y-0 motion-reduce:scale-100 motion-reduce:transition-[opacity] ${
          shown
            ? 'translate-y-0 scale-100 opacity-100 duration-spring ease-spring'
            : 'translate-y-full opacity-0 duration-exit ease-exit sm:translate-y-0 sm:scale-[0.96]'
        }`}
      >
        {v.onSubmit ? (
          <form
            noValidate
            className="flex min-h-0 flex-1 flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              if (!latest.current.busy) latest.current.onSubmit?.();
            }}
          >
            {content}
          </form>
        ) : content}
        {/* Inside the panel's React tree: its Escape stops there, so this dialog stays open. */}
        <Dialog
          open={open && asking}
          onClose={() => setAsking(false)}
          alert
          size="sm"
          title="Discard your changes?"
          initialFocus={keepRef}
          returnTo={resumeAt}
          footer={(
            <>
              <Button variant="danger-quiet" disabled={!!v.busy} onClick={() => { setAsking(false); latest.current.onClose(); }}>Discard</Button>
              <Button ref={keepRef} variant="filled" onClick={() => setAsking(false)}>Keep editing</Button>
            </>
          )}
        >
          <p className="m-0 text-sm text-on-surface-variant">Closing drops what you typed here.</p>
        </Dialog>
      </div>
    </div>,
    document.body,
  );
};

export default Dialog;
